import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { PwaOnboardingSlide } from '../../types';
import { resolveMediaUrl } from '../../utils/api';
import { setOnboardingCompleted } from '../../utils/pwaDetection';
import { Coffee, ChevronDown } from 'lucide-react';

// Ensure iOS PWA extends layout viewport behind the status bar / notch (safe-area-inset-top)
const ensureViewportFitCover = () => {
  if (typeof document === 'undefined') return;
  const viewportMeta = document.querySelector('meta[name="viewport"]');
  if (viewportMeta) {
    const content = viewportMeta.getAttribute('content') || 'width=device-width, initial-scale=1.0';
    if (!content.includes('viewport-fit=cover')) {
      viewportMeta.setAttribute('content', `${content}, viewport-fit=cover`);
    }
  }
};

ensureViewportFitCover();

interface PwaOnboardingProps {
  slides?: PwaOnboardingSlide[];
  onComplete?: () => void;
  onLogin?: () => void;
  isPreview?: boolean;
}

const DEFAULT_SLIDES: PwaOnboardingSlide[] = [
  {
    id: 'slide-1',
    order: 1,
    image: 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?auto=format&fit=crop&w=1200&q=80',
    title: 'Nikmati Kopi Spesialti Asli Dumai',
    description: 'Biji kopi pilihan nusantara yang disangrai dengan presisi untuk menghadirkan aroma dan cita rasa terbaik di setiap cangkir.',
    enabled: true,
  },
  {
    id: 'slide-2',
    order: 2,
    image: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1200&q=80',
    title: 'Pesan Mudah & Kumpulkan Poin',
    description: 'Pesan langsung dari mejamu, kumpulkan loyalty point, dan nikmati berbagai reward eksklusif member LetonKu.',
    enabled: true,
  },
  {
    id: 'slide-3',
    order: 3,
    image: 'https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?auto=format&fit=crop&w=1200&q=80',
    title: 'Kunjungi Outlet & Nikmati Suasananya',
    description: 'Temukan Chapter 5 Sudirman, Chapter 6 Kelakap 7, atau layanan Let’GO Mobile Coffee di kotamu.',
    enabled: true,
  },
];

export const PwaOnboarding: React.FC<PwaOnboardingProps> = ({
  slides: rawSlides,
  onComplete,
  onLogin,
  isPreview = false,
}) => {
  // Filter active slides and sort by order
  const activeSlides = Array.isArray(rawSlides) && rawSlides.length > 0
    ? rawSlides.filter((s) => s.enabled !== false).sort((a, b) => (a.order || 0) - (b.order || 0))
    : [];

  const displaySlides = activeSlides.length > 0 ? activeSlides : DEFAULT_SLIDES;

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [imageErrorMap, setImageErrorMap] = useState<Record<string, boolean>>({});

  // Touch swipe handling
  const touchStartXRef = useRef<number | null>(null);
  const touchDeltaXRef = useRef<number>(0);
  const isSwipingRef = useRef<boolean>(false);

  // Autoplay timer reference
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 0. ENSURE TOP VIEWPORT EDGE & iOS SAFE-AREA COVERAGE BEFORE FIRST PAINT
  useLayoutEffect(() => {
    if (isPreview) return;
    ensureViewportFitCover();
    window.scrollTo(0, 0);
  }, [isPreview]);

  // 1. PRELOAD ALL SLIDE IMAGES TO ELIMINATE LOADING LAG & BLANK FLASH
  useEffect(() => {
    if (!displaySlides || displaySlides.length === 0) return;
    displaySlides.forEach((slide) => {
      if (slide.image) {
        const img = new Image();
        img.src = resolveMediaUrl(slide.image);
      }
    });
  }, [displaySlides]);

  // 2. AUTOPLAY TIMER LIFECYCLE MANAGEMENT (~3 SECONDS INTERVAL)
  const startAutoplayTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (displaySlides.length <= 1) return;

    timerRef.current = setInterval(() => {
      if (document.visibilityState === 'hidden' || isSwipingRef.current) {
        return;
      }
      setCurrentIndex((prev) => (prev + 1) % displaySlides.length);
    }, 3000);
  }, [displaySlides.length]);

  useEffect(() => {
    startAutoplayTimer();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startAutoplayTimer();
      } else if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [startAutoplayTimer]);

  // Safe manual navigation that resets the autoplay countdown
  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    startAutoplayTimer();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchDeltaXRef.current = 0;
    isSwipingRef.current = true;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartXRef.current !== null) {
      touchDeltaXRef.current = e.touches[0].clientX - touchStartXRef.current;
    }
  };

  const handleTouchEnd = () => {
    if (touchStartXRef.current !== null) {
      const deltaX = touchDeltaXRef.current;
      const swipeThreshold = 35;

      if (deltaX < -swipeThreshold) {
        // Swiped Left -> Next slide (looping back to start)
        setCurrentIndex((prev) => (prev + 1) % displaySlides.length);
      } else if (deltaX > swipeThreshold) {
        // Swiped Right -> Previous slide (looping to end)
        setCurrentIndex((prev) => (prev - 1 + displaySlides.length) % displaySlides.length);
      }
    }
    touchStartXRef.current = null;
    touchDeltaXRef.current = 0;
    isSwipingRef.current = false;
    startAutoplayTimer();
  };

  const handleFinish = () => {
    if (!isPreview) {
      setOnboardingCompleted();
    }
    if (onComplete) {
      onComplete();
    }
  };

  const handleMasuk = () => {
    if (onLogin) {
      onLogin();
    } else {
      handleFinish();
    }
  };

  const handleSkip = () => {
    handleFinish();
  };

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        goToSlide((currentIndex + 1) % displaySlides.length);
      } else if (e.key === 'ArrowLeft') {
        goToSlide((currentIndex - 1 + displaySlides.length) % displaySlides.length);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, displaySlides.length]);

  return (
    <div
      className={`${
        isPreview
          ? 'relative w-full h-[660px] rounded-3xl overflow-hidden border border-slate-200 shadow-2xl flex flex-col'
          : 'pwa-onboarding-fullscreen z-50 overflow-y-auto overflow-x-hidden overscroll-none m-0 p-0 flex flex-col'
      } bg-white text-[#172033] select-none`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={
        isPreview
          ? undefined
          : {
              margin: 0,
              padding: 0,
              WebkitOverflowScrolling: 'touch',
            }
      }
    >
      <div
        className="w-full flex-1 shrink-0 min-h-full flex flex-col justify-between bg-white"
        style={{
          paddingTop: 0,
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
          paddingBottom: isPreview
            ? '1rem'
            : 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)',
        }}
      >
        {/* ---------------------------------------------------- */}
        {/* 1. FULL-BLEED EDGE-TO-EDGE HERO IMAGE STACK         */}
        {/* ---------------------------------------------------- */}
        <div
          className={`relative w-full m-0 p-0 overflow-hidden rounded-none border-0 ${
            isPreview
              ? 'h-[52%] shrink-0'
              : 'flex-[1.15] shrink min-h-[190px] sm:min-h-[230px] md:min-h-[270px] max-h-[55%] md:max-h-[50%]'
          }`}
          style={{
            width: '100%',
            marginTop: 0,
            paddingTop: 0,
            minHeight: isPreview
              ? undefined
              : 'calc(185px + env(safe-area-inset-top, 0px))',
          }}
        >
          {displaySlides.map((slide, idx) => {
            const isActive = idx === currentIndex;
            return (
              <div
                key={slide.id || idx}
                className={`absolute inset-0 top-0 left-0 right-0 bottom-0 w-full h-full m-0 p-0 transition-opacity duration-400 ease-out pointer-events-none ${
                  isActive ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
                style={{ willChange: 'opacity', marginTop: 0, paddingTop: 0 }}
              >
                {slide.image && !imageErrorMap[slide.id] ? (
                  <img
                    src={resolveMediaUrl(slide.image)}
                    alt={slide.title}
                    loading="eager"
                    decoding="async"
                    onError={() =>
                      setImageErrorMap((prev) => ({ ...prev, [slide.id]: true }))
                    }
                    className="w-full h-full object-cover block m-0 p-0 rounded-none border-0"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center 35%',
                      margin: 0,
                      padding: 0,
                      display: 'block',
                    }}
                  />
                ) : (
                  <div
                    className="w-full h-full bg-gradient-to-br from-slate-900 via-slate-800 to-[#0284C7] flex flex-col items-center justify-center p-6 text-center text-white m-0 rounded-none border-0"
                    style={{
                      paddingTop: isPreview
                        ? '1.5rem'
                        : 'calc(env(safe-area-inset-top, 0px) + 1.5rem)',
                    }}
                  >
                    <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center mb-3">
                      <Coffee className="w-8 h-8 text-sky-300" />
                    </div>
                    <span className="font-display font-bold text-sm text-slate-100">
                      {slide.title}
                    </span>
                  </div>
                )}
              </div>
            );
          })}

          {/* Seamless Natural Fade Transition from Image Bottom to White Page */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 sm:h-18 md:h-24 bg-gradient-to-t from-white via-white/75 to-transparent z-20" />
        </div>

        {/* ---------------------------------------------------- */}
        {/* 2. BOTTOM CONTENT & ACTIONS CONTAINER (SHRINK-0)    */}
        {/* ---------------------------------------------------- */}
        <div className="flex-1 shrink-0 w-full px-6 md:px-8 max-w-md md:max-w-xl lg:max-w-2xl mx-auto flex flex-col justify-between gap-2 sm:gap-3 md:gap-4 pt-1.5 sm:pt-2.5 md:pt-4">
          {/* Pagination Dots */}
          <div className="flex items-center justify-center gap-1.5 md:gap-2.5 py-0.5 shrink-0 z-10">
            {displaySlides.map((slide, idx) => (
              <button
                key={slide.id || idx}
                type="button"
                onClick={() => goToSlide(idx)}
                aria-label={`Ke slide ${idx + 1}`}
                className={`transition-all duration-300 cursor-pointer ${
                  idx === currentIndex
                    ? 'w-6 md:w-8 h-2 md:h-2.5 rounded-full bg-[#0284C7]'
                    : 'w-2 md:w-2.5 h-2 md:h-2.5 rounded-full bg-slate-200 hover:bg-slate-300'
                }`}
              />
            ))}
          </div>

          {/* Headline & Description Stack (Intrinsic CSS Grid Stack — Zero Clipping/Overlap) */}
          <div className="grid grid-cols-1 items-center w-full text-center my-auto py-0.5">
            {displaySlides.map((slide, idx) => {
              const isActive = idx === currentIndex;
              return (
                <div
                  key={slide.id || idx}
                  aria-hidden={!isActive}
                  className={`col-start-1 row-start-1 transition-all duration-300 ease-out space-y-1.5 sm:space-y-2 md:space-y-3 ${
                    isActive
                      ? 'opacity-100 translate-y-0 pointer-events-auto z-10'
                      : 'opacity-0 translate-y-2 pointer-events-none z-0'
                  }`}
                  style={{ willChange: 'opacity, transform' }}
                >
                  <h1 className="font-display font-black text-[22px] sm:text-2xl md:text-3xl lg:text-4xl text-[#172033] tracking-tight leading-snug">
                    {slide.title}
                  </h1>
                  <p className="text-[13.5px] sm:text-sm md:text-base lg:text-lg text-slate-700 font-medium leading-relaxed max-w-[340px] sm:max-w-md md:max-w-lg mx-auto">
                    {slide.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Country Selector & Primary / Secondary Buttons */}
          <div className="space-y-2.5 sm:space-y-3 md:space-y-3.5 pt-0.5 w-full text-center shrink-0">
            {/* Country Selector */}
            <div className="flex justify-center">
              <div className="inline-flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1 md:py-1.5 rounded-full bg-slate-100 text-slate-800 text-xs md:text-sm font-semibold select-none border border-slate-200/80 shadow-2xs">
                <span className="text-sm md:text-base leading-none">🇮🇩</span>
                <span>Indonesia</span>
                <ChevronDown className="w-3.5 h-3.5 md:w-4 md:h-4 text-slate-500" />
              </div>
            </div>

            {/* Primary Button "MASUK" — Brand Blue (#0284C7) + Pure White Text (#FFFFFF) */}
            <button
              type="button"
              onClick={handleMasuk}
              className="w-full py-3 sm:py-3.5 md:py-4 px-6 md:px-8 rounded-2xl md:rounded-3xl bg-[#0284C7] hover:bg-[#0369A1] active:scale-[0.98] text-[#FFFFFF] font-display font-bold text-base md:text-lg tracking-wider uppercase transition-all shadow-md shadow-[#0284C7]/20 cursor-pointer flex items-center justify-center"
            >
              MASUK
            </button>

            {/* Secondary Action "Lewati tahap ini" */}
            <div>
              <button
                type="button"
                onClick={handleSkip}
                className="text-slate-600 hover:text-slate-900 text-xs sm:text-[13px] md:text-sm font-semibold transition-colors cursor-pointer py-1 px-3"
              >
                Lewati tahap ini
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
